import { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Building2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email.";
    if (!form.message.trim()) e.message = "Please write a message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", message: "" });
      setErrors({});
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field) => (e) =>
    setForm((s) => ({ ...s, [field]: e.target.value }));

  return (
    <div data-testid="page-contact" className="ihm-page-enter">
      {/* Hero */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-12">
          <span className="ihm-chip">Contact</span>
          <h1
            data-testid="contact-title"
            className="mt-8 font-heading font-bold text-5xl md:text-7xl lg:text-[80px] tracking-tighter leading-[1.02]"
          >
            Let's talk.
          </h1>
          <p className="mt-8 max-w-2xl text-[18px] text-[#6E6E73] leading-relaxed">
            Have a question, an idea, or a partnership in mind? Send us a note —
            we read every message.
          </p>
        </div>
      </section>

      {/* Form + Info */}
      <section className="border-t border-[#E5E5EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Form */}
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="lg:col-span-7 ihm-card p-8 md:p-12"
              noValidate
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label
                    htmlFor="contact-name"
                    className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]"
                  >
                    Name
                  </Label>
                  <Input
                    id="contact-name"
                    data-testid="contact-input-name"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={update("name")}
                    aria-invalid={!!errors.name}
                    className="mt-3 h-12 rounded-xl bg-white border-[#E5E5EA] text-[15px] focus-visible:ring-[#2997FF]"
                  />
                  {errors.name && (
                    <p className="mt-2 text-[12px] text-[#FF3B30]" data-testid="contact-error-name">
                      {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <Label
                    htmlFor="contact-email"
                    className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]"
                  >
                    Email
                  </Label>
                  <Input
                    id="contact-email"
                    data-testid="contact-input-email"
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={update("email")}
                    aria-invalid={!!errors.email}
                    className="mt-3 h-12 rounded-xl bg-white border-[#E5E5EA] text-[15px] focus-visible:ring-[#2997FF]"
                  />
                  {errors.email && (
                    <p className="mt-2 text-[12px] text-[#FF3B30]" data-testid="contact-error-email">
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <Label
                  htmlFor="contact-message"
                  className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]"
                >
                  Message
                </Label>
                <Textarea
                  id="contact-message"
                  data-testid="contact-input-message"
                  placeholder="Tell us what you have in mind…"
                  value={form.message}
                  onChange={update("message")}
                  aria-invalid={!!errors.message}
                  className="mt-3 min-h-[180px] rounded-xl bg-white border-[#E5E5EA] text-[15px] focus-visible:ring-[#2997FF]"
                />
                {errors.message && (
                  <p className="mt-2 text-[12px] text-[#FF3B30]" data-testid="contact-error-message">
                    {errors.message}
                  </p>
                )}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
                <p className="text-[12px] text-[#86868B] max-w-sm">
                  By submitting, you agree to be contacted regarding your
                  enquiry. Your details are stored securely.
                </p>
                <Button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={submitting}
                  className="group h-12 rounded-full bg-[#1D1D1F] px-7 text-[14px] font-medium text-white hover:bg-[#2997FF] transition-all duration-500"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send message
                      <ArrowRight
                        size={16}
                        className="ml-2 transition-transform duration-500 group-hover:translate-x-1"
                      />
                    </>
                  )}
                </Button>
              </div>
            </form>

            {/* Info */}
            <aside className="lg:col-span-5 flex flex-col gap-6">
              <div className="rounded-3xl border border-[#E5E5EA] p-8">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]">
                  Registered office
                </span>
                <h3 className="mt-3 font-heading font-semibold text-2xl tracking-tight flex items-center gap-3">
                  <Building2 size={20} /> House of IHM EdTech Pvt. Ltd.
                </h3>
                <p className="mt-4 text-[15px] leading-relaxed text-[#6E6E73]">
                  Lucknow, Uttar Pradesh, India
                </p>
                <p className="mt-2 text-[13px] font-mono uppercase tracking-[0.18em] text-[#86868B]">
                  ROC · Kanpur
                </p>
              </div>

              <div className="rounded-3xl border border-[#E5E5EA] p-8">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]">
                  General enquiries
                </span>
                <a
                  href="mailto:hello@houseofihm.com"
                  data-testid="contact-info-email"
                  className="mt-3 flex items-center gap-3 font-heading font-semibold text-2xl tracking-tight ihm-underline"
                >
                  <Mail size={20} /> hello@houseofihm.com
                </a>
                <p className="mt-4 text-[14px] leading-relaxed text-[#6E6E73]">
                  We aim to respond within 2 business days.
                </p>
              </div>

              <div className="rounded-3xl bg-[#1D1D1F] text-white p-8">
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/60">
                  Headquarters
                </span>
                <h3 className="mt-3 font-heading font-semibold text-2xl tracking-tight flex items-center gap-3">
                  <MapPin size={20} className="text-[#2997FF]" /> Lucknow, India
                </h3>
                <p className="mt-4 text-[14px] leading-relaxed text-white/70">
                  Building scalable platforms in education and advanced
                  technologies — from the heart of Uttar Pradesh.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
